import React, { useState, useContext } from 'react'
import {
  Col,
  Row,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  FormGroup,
  Input,
  Label,
  Button,
  Alert,
} from 'reactstrap'
import { Controller, FormContext, useForm, ErrorMessage } from 'react-hook-form'
import { useFormUtils } from 'views/shared/useFormUtils'
import ProductContext from 'context/product/productContext'

const Reports = () => {
  const [reportParams, setReportParams] = useState({
    category: null, // 'NBS' | 'BS' -> brandMode
    type: null, // 'AUTHORIZED' | 'REVIEWED' -> reviewedMode
    startDate: null, // yyyy-mm-dd
    endDate: null, // yyyy-mm-dd
  })
  const [isExporting, setIsExporting] = useState(false)

  const { asterisk, download } = useFormUtils()
  const methods = useForm()
  const { handleSubmit, getValues } = methods
  const { exportReport, exportAuthorizedCsv } = useContext(ProductContext)

  const handleRadioChange = (field, value) => {
    setReportParams(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleExport = handleSubmit(async () => {
    const common = {
      brandMode: reportParams.category || 'ALL',
      from: reportParams.startDate || undefined,
      to: reportParams.endDate || undefined,
      offset: 0,
      fetch: 50000,
    }

    if (reportParams.type === 'AUTHORIZED') {
      await exportAuthorizedCsv(common) // CSV path
    } else if (reportParams.type === 'REVIEWED') {
      await exportReport({
        // XLSX path (multi-sheet)
        ...common,
        reviewedMode: 'REVIEWED',
      })
    } else {
      // Shouldn't happen due to form validation, but just in case:
      alert('Please select a report type.')
    }
  })

  const currentDate = new Date()
  const today =
    currentDate.getFullYear() +
    '-' +
    ('0' + (currentDate.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + currentDate.getDate()).slice(-2)

  // NEW: helper—safe lexicographic compare for YYYY-MM-DD
  const isAfterToday = v => v && v > today

  const onExport = async () => {
    setIsExporting(true)
    try {
      await exportReport({
        brandMode: reportParams.category || 'ALL',
        reviewedMode: reportParams.type || 'AUTHORIZED',
        from: reportParams.startDate || undefined,
        to: reportParams.endDate || undefined,
        offset: 0,
        fetch: 50000,
      })
    } catch (err) {
      alert(`Export failed: ${err.message}`)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="content">
      <FormContext {...methods}>
        {/* prevent native submit; validation runs via handleSubmit(onExport) */}
        <form className="Form" onSubmit={e => e.preventDefault()}>
          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <CardTitle className="float-left" tag="h4">
                    Reports
                  </CardTitle>
                </CardHeader>

                <Col xs={6}>
                  <CardBody className="pl-0">
                    {/* ===== Report Category (Brand Mode) ===== */}
                    <FormGroup>
                      <h6 className="text-info">
                        Select Report Category {asterisk}
                      </h6>
                    </FormGroup>

                    <FormGroup check className="ml-5">
                      <Input
                        className="form-check-input"
                        type="radio"
                        name="category"
                        id="category1"
                        value="NBS"
                        checked={reportParams.category === 'NBS'}
                        innerRef={methods.register({
                          required: '⚠ Please select a report category.',
                        })}
                        onChange={e =>
                          handleRadioChange('category', e.target.value)
                        }
                      />
                      <Label check htmlFor="category1" className="text-label">
                        Any Brand
                      </Label>
                    </FormGroup>

                    <FormGroup check className="ml-5">
                      <Input
                        className="form-check-input"
                        type="radio"
                        name="category"
                        id="category2"
                        value="BS"
                        checked={reportParams.category === 'BS'}
                        innerRef={methods.register({
                          required: '⚠ Please select a report category.',
                        })}
                        onChange={e =>
                          handleRadioChange('category', e.target.value)
                        }
                      />
                      <Label check htmlFor="category2" className="text-label">
                        Brand Specific
                      </Label>
                    </FormGroup>

                    {/* Error for category */}
                    <div className="mt-2">
                      <ErrorMessage
                        errors={methods.errors}
                        name="category"
                        as={<Alert color="danger" className="mt-1 mb-0" />}
                      />
                    </div>

                    {/* ===== Report Type (Reviewed Mode) ===== */}
                    <FormGroup className="mt-3">
                      <h6 className="text-info">
                        Submitter Report Type {asterisk}
                      </h6>
                    </FormGroup>

                    <FormGroup check className="ml-5">
                      <Input
                        type="radio"
                        id="type1"
                        name="type"
                        value="AUTHORIZED"
                        checked={reportParams.type === 'AUTHORIZED'}
                        innerRef={methods.register({
                          required: '⚠ Please select a report type.',
                        })}
                        onChange={e =>
                          handleRadioChange('type', e.target.value)
                        }
                      />
                      <Label check htmlFor="type1" className="text-label">
                        Authorized Products
                      </Label>
                    </FormGroup>

                    <FormGroup check className="ml-5">
                      <Input
                        type="radio"
                        id="type2"
                        name="type"
                        value="REVIEWED"
                        checked={reportParams.type === 'REVIEWED'}
                        innerRef={methods.register({
                          required: '⚠ Please select a report type.',
                        })}
                        onChange={e =>
                          handleRadioChange('type', e.target.value)
                        }
                      />
                      <Label check htmlFor="type2" className="text-label">
                        All Reviewed Products
                      </Label>
                    </FormGroup>

                    {/* Error for type */}
                    <div className="mt-2">
                      <ErrorMessage
                        errors={methods.errors}
                        name="type"
                        as={<Alert color="danger" className="mt-1 mb-0" />}
                      />
                    </div>

                    {/* ===== Dates ===== */}
                    <FormGroup className="mt-3">
                      <h6 className="text-info">Report Start Date</h6>
                      <Controller
                        as={Input}
                        type="date"
                        max={today}
                        name="startDate"
                        defaultValue=""
                        control={methods.control}
                        rules={{
                          validate: v => {
                            const end = getValues('endDate')

                            // NEW: forbid future start date
                            if (v && isAfterToday(v)) {
                              return '⚠ Start date cannot be in the future.'
                            }

                            if (!v && end)
                              return '⚠ Please enter a Report Start Date.'
                            if (v && end && v > end)
                              return '⚠ Please enter a valid start date.'
                            return true
                          },
                        }}
                        onChange={([arg]) => {
                          const value = arg?.target?.value ?? arg
                          setReportParams(prev => ({
                            ...prev,
                            startDate: value || '',
                          }))
                          return value
                        }}
                      />
                      <ErrorMessage
                        errors={methods.errors}
                        name="startDate"
                        as={<Alert color="danger" className="mt-2 mb-0" />}
                      />
                    </FormGroup>

                    <FormGroup>
                      <h6 className="text-info">Report End Date</h6>
                      <Controller
                        as={Input}
                        type="date"
                        max={today}
                        name="endDate"
                        defaultValue=""
                        control={methods.control}
                        rules={{
                          validate: v => {
                            const start = getValues('startDate')

                            // NEW: forbid future end date
                            if (v && isAfterToday(v)) {
                              return '⚠ End date cannot be in the future.'
                            }

                            if (!v && start)
                              return '⚠ Please enter a Report End Date.'
                            if (v && start && v < start)
                              return '⚠ Please enter a valid end date.'
                            return true
                          },
                        }}
                        onChange={([arg]) => {
                          const value = arg?.target?.value ?? arg
                          setReportParams(prev => ({
                            ...prev,
                            endDate: value || '',
                          }))
                          return value
                        }}
                      />
                      <ErrorMessage
                        errors={methods.errors}
                        name="endDate"
                        as={<Alert color="danger" className="mt-2 mb-0" />}
                      />
                    </FormGroup>

                    {/* ===== Export ===== */}
                    {/* <Button
                      type="button"
                      className="btn-round button-report"
                      disabled={isExporting}
                      onClick={handleSubmit(onExport)} // triggers validation
                    >
                      {download} {isExporting ? 'Exporting…' : 'Export Report'}
                    </Button> */}
                    <Button
                      type="button"
                      className="btn-round button-report"
                      disabled={isExporting}
                      onClick={async () => {
                        setIsExporting(true)
                        try {
                          await handleExport()
                        } catch (err) {
                          alert(
                            `There are no products ${
                              reportParams.type === 'AUTHORIZED'
                                ? 'approved'
                                : 'reviewed'
                            } during this time range`
                          )
                        } finally {
                          setIsExporting(false)
                        }
                      }}
                    >
                      {download} {isExporting ? 'Exporting..' : 'Export Report'}
                    </Button>
                  </CardBody>
                </Col>
              </Card>
            </Col>
          </Row>
        </form>
      </FormContext>
    </div>
  )
}

export default Reports
